import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import dayjs, { Dayjs } from 'dayjs';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

export function createDateRangeValidator(dateRange: NgbDateStruct[]): ValidatorFn {
  function getValidDate(date): dayjs.Dayjs | false {
    if (dayjs(date, 'YYYY-MM-DD', true).isValid()) {
      return dayjs(date, 'YYYY-MM-DD', true);
    }
    if (dayjs(date, 'DD.MM.YYYY', true).isValid()) {
      return dayjs(date, 'DD.MM.YYYY', true);
    }
    return false;
  }

  function dayjsFromNgbDate(date: NgbDateStruct): dayjs.Dayjs {
    return dayjs(`${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`, 'YYYY-MM-DD', true);
  }

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.toString()?.trim();
    const valueAsValidDate = getValidDate(value);
    if (!value || !valueAsValidDate) return { registrationTimeRange: true };
    const registrationTimeRangeDayjs = dateRange.map(e => {
      return dayjsFromNgbDate(e);
    });
    const dateValid =
      valueAsValidDate.valueOf() >= registrationTimeRangeDayjs[0].valueOf() &&
      valueAsValidDate.valueOf() <= registrationTimeRangeDayjs[1].valueOf();

    return !dateValid ? { registrationTimeRange: true } : null;
  };
}

/**
 *
 * @param controlName
 * @param comparator
 * @returns
 */
export function datePickerCompareValidator(
  controlName: string,
  comparators?: Array<(thisDate: Dayjs, comparedData: Dayjs) => ReturnType<ValidatorFn>>,
) {
  return (control: AbstractControl) => {
    const controlToCompareTo = control.root.get(controlName);

    if (!controlToCompareTo) {
      return null;
    }

    const controlToCompareToDate = dayjs(controlToCompareTo.value, 'DD.MM.YYYY');
    const controlDate = dayjs(control.value, 'DD.MM.YYYY');
    if (!controlToCompareToDate.isValid()) {
      controlToCompareTo.setErrors({ invalidDate: true });
      return null;
    }

    if (!controlDate.isValid()) {
      return { invalidDate: true };
    }

    if (comparators) {
      const validationErrors = comparators.reduce<ReturnType<ValidatorFn>>((acc, comparator) => {
        const result = comparator(controlDate, controlToCompareToDate);
        if (result) {
          return { ...(acc ?? {}), ...result };
        }
        return acc;
      }, null);
      if (validationErrors) {
        return validationErrors;
      }
      if (!controlToCompareTo.valid && !validationErrors) {
        controlToCompareTo.updateValueAndValidity();
      }
    } else if (controlDate.isBefore(controlToCompareToDate)) {
      return { isBeforeCompareDate: true };
    }
    if (!controlToCompareTo.valid) {
      controlToCompareTo.updateValueAndValidity();
    }
    return null;
  };
}

export function isNotBeforeComparedDateComparator(thisDate: Dayjs, comparedData: Dayjs): ReturnType<ValidatorFn> {
  if (thisDate.isBefore(comparedData)) {
    return { isBeforeCompareDate: true };
  }
  return null;
}

export function isNotAfterComparedDateComparator(thisDate: Dayjs, comparedData: Dayjs): ReturnType<ValidatorFn> {
  if (thisDate.isAfter(comparedData)) {
    return { isAfterCompareDate: true };
  }
  return null;
}

/**
 * This function validates if a date is in the correct format and valid.
 * This additional function is necessary because datePickerCompareValidator still returns null even if the date is not valid.
 *
 * @param control of specified IFormInput.
 * @returns null if date is valid and otherwise and error with the error key 'isValidDate'.
 */
export const isValidDate: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  dayjs(control.value, 'DD.MM.YYYY', true).isValid() ? null : { isValidDate: true };
